import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SEO from '@/components/SEO'
import {
    Download,
    Terminal,
    Check,
    Copy,
    ShieldAlert,
    HelpCircle,
    ChevronDown,
    ChevronUp,
    Laptop,
    Lock,
    Sparkles
} from 'lucide-react'

type Platform = 'mac' | 'windows' | 'linux' | 'unknown'

/* ── OS Detection Helper ────────────────────────────────────────────────── */
function detectOS(): Platform {
    if (typeof window === 'undefined') return 'unknown'
    const ua = navigator.userAgent.toLowerCase()
    const platform = navigator.platform.toLowerCase()

    if (ua.includes('mac') || platform.includes('mac')) return 'mac'
    if (ua.includes('win') || platform.includes('win')) return 'windows'
    if (ua.includes('linux') || platform.includes('linux')) return 'linux'
    return 'unknown'
}

/* ── Terminal-style label ──────────────────────────────────────────────── */
function Label({ children }: { children: React.ReactNode }) {
    return (
        <p className="text-[10px] font-mono text-[#555] mb-1.5 flex items-center gap-1.5">
            <span className="text-primary">$</span> {children}
        </p>
    )
}

/* ── Step Badge Component ──────────────────────────────────────────────── */
function StepBadge({ n, active = true }: { n: number; active?: boolean }) {
    return (
        <span
            className={[
                'shrink-0 w-6 h-6 rounded-sm text-[11px] font-mono flex items-center justify-center border transition-colors duration-300',
                active
                    ? 'border-primary/50 text-primary bg-primary/10 font-bold'
                    : 'border-[#222] text-[#444]',
            ].join(' ')}
        >
            {n}
        </span>
    )
}

export default function ArduinoConnectPage() {
    const [userOS, setUserOS] = useState<Platform>('unknown')
    const [macGuideOpen, setMacGuideOpen] = useState(false)
    const [winGuideOpen, setWinGuideOpen] = useState(false)
    const [copiedCmd, setCopiedCmd] = useState<'sh' | 'ps1' | null>(null)

    useEffect(() => {
        const os = detectOS()
        setUserOS(os)
        // Auto-open relevant OS guidance if helpful
        if (os === 'mac') setMacGuideOpen(true)
        if (os === 'windows') setWinGuideOpen(true)
    }, [])

    const copyScriptCmd = async (type: 'sh' | 'ps1') => {
        const text = type === 'sh'
            ? 'curl -fsSL https://www.raphealgadoh.me/install.sh | bash'
            : 'irm https://www.raphealgadoh.me/install.ps1 | iex'
        await navigator.clipboard.writeText(text)
        setCopiedCmd(type)
        setTimeout(() => setCopiedCmd(null), 2500)
    }

    return (
        <div className="min-h-screen font-mono py-12">
            <SEO
                title="Arduino GitHub Pusher IDE Connect | One-Click Sketch Pusher"
                description="Connect Arduino IDE 2.x and VS Code to GitHub with 1-click installer scripts and token setup for Arduino GitHub Pusher."
                canonical="https://connect.raphealgadoh.me/"
            />
            <div className="max-w-3xl mx-auto px-6 space-y-10">

                {/* ── Page Header ── */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider rounded-sm bg-primary/10 text-primary border border-primary/20">
                            Zero-Website Setup
                        </span>
                        <span className="text-[10px] text-[#555]">
                            macOS & Windows Compatible
                        </span>
                    </div>

                    <h1 className="text-sm sm:text-base mb-1">
                        <span className="text-primary">rapheal@portfolio</span>
                        <span className="text-[#444]">:~$</span>
                        <span className="text-foreground ml-2 font-bold">download --arduino-github-pusher</span>
                    </h1>
                    <p className="text-xs text-[#666] leading-relaxed">
                        // No account creation or website logins required. Download, launch, authorize on GitHub.com via Device Code, and push sketches directly from Arduino IDE.
                    </p>
                </motion.div>

                {/* ── Zero-Stress Architecture Overview Card ── */}
                <motion.div
                    className="terminal-window"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: 0.1 }}
                >
                    <div className="terminal-titlebar">
                        <span className="terminal-dot bg-[#ff5f57]" />
                        <span className="terminal-dot bg-[#ffbd2e]" />
                        <span className="terminal-dot bg-[#28c840]" />
                        <span className="ml-3 text-[10px] text-[#444]">how-it-works.md</span>
                    </div>

                    <div className="p-6 space-y-6">
                        <div className="flex items-center justify-between border-b border-[#111] pb-3">
                            <h2 className="text-xs font-semibold text-foreground flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-primary" />
                                4-Step Zero-Stress Setup (No Website Token Entry!)
                            </h2>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4 text-xs">
                            <div className="term-card space-y-2">
                                <div className="flex items-center gap-2">
                                    <StepBadge n={1} />
                                    <span className="font-semibold text-foreground">Download App / Extension</span>
                                </div>
                                <p className="text-[11px] text-[#666] leading-relaxed">
                                    Grab the installer or ZIP package below for your operating system.
                                </p>
                            </div>

                            <div className="term-card space-y-2">
                                <div className="flex items-center gap-2">
                                    <StepBadge n={2} />
                                    <span className="font-semibold text-foreground">Launch & Device Auth</span>
                                </div>
                                <p className="text-[11px] text-[#666] leading-relaxed">
                                    The app displays a code (e.g. <code className="text-primary bg-primary/10 px-1 rounded">ABCD-1234</code>) and auto-opens <span className="text-foreground font-semibold">github.com/login/device</span>.
                                </p>
                            </div>

                            <div className="term-card space-y-2">
                                <div className="flex items-center gap-2">
                                    <StepBadge n={3} />
                                    <span className="font-semibold text-foreground">Approve Directly on GitHub</span>
                                </div>
                                <p className="text-[11px] text-[#666] leading-relaxed">
                                    Approve authorization on GitHub. Your token is stored safely in your OS Keychain / Windows Vault.
                                </p>
                            </div>

                            <div className="term-card space-y-2">
                                <div className="flex items-center gap-2">
                                    <StepBadge n={4} />
                                    <span className="font-semibold text-foreground">Auto-Push on Save</span>
                                </div>
                                <p className="text-[11px] text-[#666] leading-relaxed">
                                    Whenever you press <span className="text-foreground font-semibold">Cmd+S</span> or <span className="text-foreground font-semibold">Ctrl+S</span> in Arduino IDE, your sketch is pushed to GitHub!
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* ── Direct Downloads Section ── */}
                <motion.div
                    className="space-y-4"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: 0.2 }}
                >
                    <Label>select-package --platform={userOS}</Label>

                    <div className="grid sm:grid-cols-2 gap-4">

                        {/* macOS Package */}
                        <div className={[
                            'term-card space-y-4 relative overflow-hidden transition-all duration-300',
                            userOS === 'mac' ? 'border-primary/50 bg-primary/5 shadow-lg shadow-primary/5' : 'border-[#181818]'
                        ].join(' ')}>
                            {userOS === 'mac' && (
                                <div className="absolute top-0 right-0 bg-primary text-[#050505] text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider rounded-bl-sm">
                                    Detected OS
                                </div>
                            )}

                            <div className="flex items-center gap-2.5 pb-2 border-b border-[#111]">
                                <Laptop className="w-4 h-4 text-primary" />
                                <div>
                                    <h3 className="text-xs text-foreground font-bold">macOS Package</h3>
                                    <p className="text-[10px] text-[#555]">Apple Silicon (M1/M2/M3) & Intel Mac</p>
                                </div>
                            </div>

                            <p className="text-[11px] text-[#666] leading-relaxed">
                                Universal macOS executable bundle with background sketch folder watcher.
                            </p>

                            <div className="space-y-2 pt-1">
                                <a
                                    href="/arduino-github-pusher-mac.zip"
                                    download
                                    className="btn-term-accent text-xs flex items-center justify-center gap-2 w-full py-2.5"
                                >
                                    <Download className="w-3.5 h-3.5" />
                                    [ Download for macOS (.zip) ]
                                </a>

                                <button
                                    onClick={() => setMacGuideOpen(!macGuideOpen)}
                                    className="w-full flex items-center justify-between text-[10px] text-[#666] hover:text-foreground pt-1 transition-colors cursor-pointer"
                                >
                                    <span className="flex items-center gap-1.5">
                                        <ShieldAlert className="w-3 h-3 text-amber-500" />
                                        macOS "Damaged / Gatekeeper" Notice
                                    </span>
                                    {macGuideOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                </button>
                            </div>

                            {/* Collapsible macOS Gatekeeper Guide */}
                            <AnimatePresence>
                                {macGuideOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="text-[10px] text-[#777] bg-[#080808] border border-[#1a1a1a] rounded-sm p-3 space-y-2 leading-relaxed"
                                    >
                                        <p className="text-amber-400 font-semibold flex items-center gap-1">
                                            <HelpCircle className="w-3 h-3 shrink-0" />
                                            If macOS says: "App is damaged & can't be opened"
                                        </p>
                                        <p>
                                            Because this ZIP is downloaded from the browser, macOS sets the security quarantine flag.
                                        </p>
                                        <div className="space-y-1 bg-[#050505] p-2 border border-[#151515] rounded-sm">
                                            <p className="text-foreground font-semibold">Easy Fix (No Stress):</p>
                                            <p>1. Right-click the downloaded app icon and click <span className="text-primary font-semibold">Open</span>.</p>
                                            <p>2. Or open Terminal and run:</p>
                                            <code className="block text-primary/80 bg-[#000] p-1.5 rounded mt-1 break-all font-mono">
                                                xattr -cr ~/Downloads/arduino-github-pusher.app
                                            </code>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Windows Package */}
                        <div className={[
                            'term-card space-y-4 relative overflow-hidden transition-all duration-300',
                            userOS === 'windows' ? 'border-primary/50 bg-primary/5 shadow-lg shadow-primary/5' : 'border-[#181818]'
                        ].join(' ')}>
                            {userOS === 'windows' && (
                                <div className="absolute top-0 right-0 bg-primary text-[#050505] text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider rounded-bl-sm">
                                    Detected OS
                                </div>
                            )}

                            <div className="flex items-center gap-2.5 pb-2 border-b border-[#111]">
                                <Laptop className="w-4 h-4 text-primary" />
                                <div>
                                    <h3 className="text-xs text-foreground font-bold">Windows Package</h3>
                                    <p className="text-[10px] text-[#555]">Windows 10 / 11 (64-bit)</p>
                                </div>
                            </div>

                            <p className="text-[11px] text-[#666] leading-relaxed">
                                Portable executable & installer bundle for Windows desktops.
                            </p>

                            <div className="space-y-2 pt-1">
                                <a
                                    href="/arduino-github-pusher-windows.zip"
                                    download
                                    className="btn-term-accent text-xs flex items-center justify-center gap-2 w-full py-2.5"
                                >
                                    <Download className="w-3.5 h-3.5" />
                                    [ Download for Windows (.zip) ]
                                </a>

                                <button
                                    onClick={() => setWinGuideOpen(!winGuideOpen)}
                                    className="w-full flex items-center justify-between text-[10px] text-[#666] hover:text-foreground pt-1 transition-colors cursor-pointer"
                                >
                                    <span className="flex items-center gap-1.5">
                                        <ShieldAlert className="w-3 h-3 text-amber-500" />
                                        Windows Defender "SmartScreen" Notice
                                    </span>
                                    {winGuideOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                </button>
                            </div>

                            {/* Collapsible Windows SmartScreen Guide */}
                            <AnimatePresence>
                                {winGuideOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="text-[10px] text-[#777] bg-[#080808] border border-[#1a1a1a] rounded-sm p-3 space-y-2 leading-relaxed"
                                    >
                                        <p className="text-amber-400 font-semibold flex items-center gap-1">
                                            <HelpCircle className="w-3 h-3 shrink-0" />
                                            If Windows says: "Windows protected your PC"
                                        </p>
                                        <p>
                                            Microsoft Defender displays a warning for newly downloaded executables.
                                        </p>
                                        <div className="space-y-1 bg-[#050505] p-2 border border-[#151515] rounded-sm">
                                            <p className="text-foreground font-semibold">Easy Fix (No Stress):</p>
                                            <p>1. Click <span className="text-primary font-semibold">More info</span> on the blue prompt.</p>
                                            <p>2. Click the <span className="text-primary font-semibold">Run anyway</span> button at the bottom.</p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                    </div>

                    {/* VSIX Alternative for Arduino IDE 2.x & VS Code */}
                    <div className="term-card space-y-4 mt-6">
                        <div className="flex items-center justify-between pb-2 border-b border-[#111]">
                            <div className="flex items-center gap-2">
                                <Download className="w-4 h-4 text-primary" />
                                <div>
                                    <h3 className="text-xs text-foreground font-bold">Arduino IDE 2.x / VS Code Extension (.vsix)</h3>
                                    <p className="text-[10px] text-[#555]">Zero OS Security Warnings Option</p>
                                </div>
                            </div>
                            <span className="text-[9px] font-mono text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-sm">
                                Recommended for Arduino IDE 2.x
                            </span>
                        </div>

                        <p className="text-[11px] text-[#666] leading-relaxed">
                            Installing as an IDE extension avoids macOS Gatekeeper and Windows Defender prompts completely, because the extension runs natively inside Arduino IDE 2.x / VS Code!
                        </p>

                        <div className="grid sm:grid-cols-2 gap-4 text-[10px] text-[#666]">
                            <div className="space-y-1 bg-[#080808] p-3 border border-[#151515] rounded-sm">
                                <p className="text-primary font-semibold mb-1">Arduino IDE 2.x Setup</p>
                                <p>1. Download the <code className="text-foreground">.vsix</code> below</p>
                                <p>2. Unzip into <code className="text-foreground">~/.arduinoIDE/plugins/</code></p>
                                <p>3. Restart Arduino IDE 2.x</p>
                            </div>
                            <div className="space-y-1 bg-[#080808] p-3 border border-[#151515] rounded-sm">
                                <p className="text-primary font-semibold mb-1">VS Code Setup</p>
                                <p>1. Download the <code className="text-foreground">.vsix</code> below</p>
                                <p>2. Press <code className="text-foreground">Ctrl+Shift+P</code> → <span className="text-foreground">Extensions: Install from VSIX…</span></p>
                                <p>3. Select the file & enjoy!</p>
                            </div>
                        </div>

                        <div className="pt-2">
                            <a
                                href="/arduino-github-pusher-1.0.0.vsix"
                                download
                                className="btn-term-accent text-xs flex items-center justify-center gap-2 w-fit"
                            >
                                <Download className="w-3.5 h-3.5" />
                                [ Download arduino-github-pusher-1.0.0.vsix ]
                            </a>
                        </div>
                    </div>

                    {/* 1-Line Terminal Installers */}
                    <div className="term-card space-y-4">
                        <div className="flex items-center gap-2 pb-2 border-b border-[#111]">
                            <Terminal className="w-4 h-4 text-primary" />
                            <h3 className="text-xs text-foreground font-bold">Terminal 1-Line Installer Scripts</h3>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            {/* macOS / Linux Terminal */}
                            <div className="space-y-2">
                                <div className="flex justify-between items-center text-[10px]">
                                    <span className="text-foreground font-semibold">macOS / Linux Terminal</span>
                                    <button
                                        onClick={() => copyScriptCmd('sh')}
                                        className="text-primary hover:underline flex items-center gap-1 font-mono cursor-pointer"
                                    >
                                        {copiedCmd === 'sh' ? <><Check className="w-3 h-3" /> copied</> : <><Copy className="w-3 h-3" /> copy</>}
                                    </button>
                                </div>
                                <code className="block text-[10px] bg-[#050505] border border-[#181818] rounded-sm p-2.5 text-primary/90 leading-relaxed break-all font-mono">
                                    curl -fsSL https://www.raphealgadoh.me/install.sh | bash
                                </code>
                            </div>

                            {/* Windows PowerShell */}
                            <div className="space-y-2">
                                <div className="flex justify-between items-center text-[10px]">
                                    <span className="text-foreground font-semibold">Windows PowerShell</span>
                                    <button
                                        onClick={() => copyScriptCmd('ps1')}
                                        className="text-primary hover:underline flex items-center gap-1 font-mono cursor-pointer"
                                    >
                                        {copiedCmd === 'ps1' ? <><Check className="w-3 h-3" /> copied</> : <><Copy className="w-3 h-3" /> copy</>}
                                    </button>
                                </div>
                                <code className="block text-[10px] bg-[#050505] border border-[#181818] rounded-sm p-2.5 text-primary/90 leading-relaxed break-all font-mono">
                                    irm https://www.raphealgadoh.me/install.ps1 | iex
                                </code>
                            </div>
                        </div>
                    </div>

                </motion.div>

                {/* ── Security & Privacy Guarantees ── */}
                <motion.div
                    className="term-card text-xs space-y-3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                >
                    <div className="flex items-center gap-2 text-primary font-semibold">
                        <Lock className="w-4 h-4" />
                        <span>// Security & Privacy Guarantees</span>
                    </div>
                    <ul className="text-[#666] leading-relaxed space-y-1.5 text-[11px] list-disc list-inside">
                        <li><span className="text-foreground font-semibold">Zero Website Intermediary</span>: Authentication connects directly from your machine to <span className="text-foreground">github.com/login/device</span>.</li>
                        <li><span className="text-foreground font-semibold">Secure Credential Storage</span>: Access tokens are stored locally in your native OS Keychain (macOS) or Windows Credential Manager.</li>
                        <li><span className="text-foreground font-semibold">Local Sketch Privacy</span>: Your Arduino sketches remain strictly on your computer and your personal GitHub repositories.</li>
                    </ul>
                </motion.div>

            </div>
        </div>
    )
}
